<script setup lang="ts">
import { useUserStore } from '@/stores/userStore';

/** The AI web app serves the support chat (with the Backtesting context). */
const WEBAPP_URL = (import.meta.env.VITE_WEBAPP_URL as string) || 'https://ai.xiomex.fund';
const SUPABASE_URL = 'https://supabase.xiomex.fund/rest/v1';
const SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.ewogICJyb2xlIjogImFub24iLAogICJpc3MiOiAic3VwYWJhc2UiLAogICJpYXQiOiAxNzI4ODc4NDAwLAogICJleHAiOiAxODg2NjQ0ODAwCn0.0C56OOhuOREfXTNIdYPUWmegoQSWUkpb5yqa0-FmgpI';
const TELEGRAM_URL = 'https://t.me/soportexiomex';
const MAX_INPUT_HEIGHT = 120;
const ERROR_MESSAGE = 'Sorry, I ran into an error. Please try again.';

const suggestions = [
  'What does Smart Backtesting do?',
  'How do I run a manual backtest?',
  'My backtest failed, what can I do?',
];

type Message = { role: 'user' | 'assistant'; content: string };

const userStore = useUserStore();
const open = ref(false);
const input = ref('');
const messages = ref<Message[]>([]);
const isLoading = ref(false);
const whatsapp = ref('');
const scrollEl = ref<HTMLElement | null>(null);
const inputEl = ref<HTMLTextAreaElement | null>(null);
const orbEl = ref<HTMLVideoElement | null>(null);
let abortController: AbortController | null = null;

const whatsappUrl = computed(
  () =>
    `https://api.whatsapp.com/send/?phone=${whatsapp.value}&text&type=phone_number&app_absent=0`,
);

async function fetchWhatsapp() {
  try {
    const response = await fetch(`${SUPABASE_URL}/settings?select=whatsapp&limit=1`, {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
    });
    if (!response.ok) return;
    const data: { whatsapp: string | null }[] = await response.json();
    whatsapp.value = data?.[0]?.whatsapp || '';
  } catch (error) {
    console.error('Error fetching whatsapp number:', error);
  }
}

watch(open, (isOpen) => {
  if (isOpen) {
    if (!whatsapp.value) fetchWhatsapp();
    // Focus the input once the open transition has started.
    setTimeout(() => inputEl.value?.focus(), 200);
  } else {
    // Make sure the launcher orb keeps playing once the panel is closed.
    orbEl.value?.play().catch(() => {});
  }
});

useEventListener(window, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape' && open.value) open.value = false;
});

watch(input, () => {
  nextTick(() => {
    const el = inputEl.value;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT)}px`;
  });
});

function scrollToBottom() {
  nextTick(() =>
    scrollEl.value?.scrollTo({ top: scrollEl.value.scrollHeight, behavior: 'smooth' }),
  );
}

function setReply(content: string) {
  messages.value[messages.value.length - 1] = { role: 'assistant', content };
  scrollToBottom();
}

async function send(text: string) {
  const content = text.trim();
  if (!content || isLoading.value) return;
  input.value = '';

  const history: Message[] = [...messages.value, { role: 'user', content }];
  messages.value = [...history, { role: 'assistant', content: '' }];
  isLoading.value = true;
  scrollToBottom();
  abortController = new AbortController();

  let reply = '';
  try {
    const response = await fetch(`${WEBAPP_URL}/api/support-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ app: 'frequi', messages: history }),
      signal: abortController.signal,
    });
    if (!response.ok || !response.body) throw new Error(`Support chat failed: ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      reply += decoder.decode(value, { stream: true });
      setReply(reply);
    }
    if (!reply.trim()) setReply(ERROR_MESSAGE);
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') {
      // Stopped before the first token: drop the empty reply.
      if (!reply) messages.value = messages.value.slice(0, -1);
      return;
    }
    console.error('Error in support chat:', error);
    setReply(ERROR_MESSAGE);
  } finally {
    abortController = null;
    isLoading.value = false;
  }
}

function stop() {
  abortController?.abort();
}

function reset() {
  abortController?.abort();
  messages.value = [];
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    send(input.value);
  }
}
</script>

<template>
  <div v-if="userStore.hasUserId" class="text-left">
    <!-- Chat panel: anchored to the launcher on desktop, full screen on mobile -->
    <section
      role="dialog"
      aria-label="Xiomex AI Agent"
      :aria-hidden="!open"
      :inert="!open || undefined"
      class="support-panel fixed inset-0 z-[60] flex flex-col overflow-hidden bg-background sm:inset-auto sm:bottom-28 sm:right-8 sm:h-[min(680px,calc(100dvh-12rem))] sm:w-[400px] sm:rounded-3xl sm:border sm:border-border sm:shadow-2xl sm:shadow-black/30"
      :class="
        open
          ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
          : 'pointer-events-none translate-y-6 opacity-0 sm:translate-y-4 sm:scale-95'
      "
    >
      <!-- Header -->
      <header
        class="relative flex shrink-0 items-center gap-3 border-b border-border/60 px-4 py-3.5"
      >
        <div class="support-glow pointer-events-none absolute inset-x-0 -top-24 h-40" />
        <div class="relative">
          <span
            class="flex h-10 w-10 items-center justify-center rounded-full border border-primary/30 bg-primary/10"
          >
            <img src="@/assets/xiomex-logo-min.png" alt="" class="support-logo h-5 w-5" />
          </span>
          <span
            class="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background"
          />
        </div>
        <div class="relative min-w-0 flex-1">
          <p class="truncate text-sm font-semibold text-foreground">Xiomex AI Agent</p>
          <p class="truncate text-xs text-muted-foreground">Online · Replies instantly</p>
        </div>
        <div class="relative flex items-center gap-1">
          <button
            v-if="messages.length > 0"
            type="button"
            aria-label="New conversation"
            title="New conversation"
            class="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground cursor-pointer"
            @click="reset"
          >
            <i-mdi-restart class="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Close"
            title="Close"
            class="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground cursor-pointer"
            @click="open = false"
          >
            <i-mdi-close class="h-4 w-4" />
          </button>
        </div>
      </header>

      <!-- Messages -->
      <div ref="scrollEl" class="support-scroll min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-5">
        <div class="flex items-end gap-2">
          <span
            class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10"
          >
            <img src="@/assets/xiomex-logo-min.png" alt="" class="support-logo h-3.5 w-3.5" />
          </span>
          <div
            class="max-w-[85%] rounded-2xl rounded-bl-md bg-muted px-3.5 py-2.5 text-sm leading-relaxed text-foreground"
          >
            Hi! I'm the Xiomex Labs AI agent. Ask me anything about backtesting, your results or
            your strategies.
          </div>
        </div>

        <div v-if="messages.length === 0" class="space-y-2 pl-9">
          <button
            v-for="(question, index) in suggestions"
            :key="question"
            type="button"
            :style="{ animationDelay: `${index * 60}ms` }"
            class="fade-up block w-full rounded-2xl border border-border px-3.5 py-2.5 text-left text-sm text-foreground transition-colors duration-200 hover:border-primary/50 hover:bg-primary/5 cursor-pointer"
            @click="send(question)"
          >
            {{ question }}
          </button>
        </div>

        <template v-for="(message, index) in messages" :key="index">
          <div v-if="message.role === 'user'" class="fade-up flex justify-end">
            <div
              class="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary px-3.5 py-2.5 text-sm leading-relaxed text-primary-foreground"
            >
              {{ message.content }}
            </div>
          </div>
          <div v-else class="fade-up flex items-end gap-2">
            <span
              class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10"
            >
              <img src="@/assets/xiomex-logo-min.png" alt="" class="support-logo h-3.5 w-3.5" />
            </span>
            <div
              class="min-w-0 max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-bl-md bg-muted px-3.5 py-2.5 text-sm leading-relaxed text-foreground"
            >
              <template v-if="message.content">{{ message.content }}</template>
              <span v-else class="flex items-center gap-1 py-1.5" aria-hidden="true">
                <span
                  v-for="delay in [0, 150, 300]"
                  :key="delay"
                  class="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/70"
                  :style="{ animationDelay: `${delay}ms` }"
                />
              </span>
            </div>
          </div>
        </template>
      </div>

      <!-- Composer -->
      <footer class="shrink-0 space-y-3 border-t border-border/60 px-3 pb-4 pt-3 sm:px-4">
        <form
          class="flex items-end gap-2 rounded-3xl border border-border bg-muted/30 py-1.5 pl-4 pr-1.5 transition-colors focus-within:border-primary/50 focus-within:bg-background"
          @submit.prevent="send(input)"
        >
          <textarea
            ref="inputEl"
            v-model="input"
            rows="1"
            placeholder="Send a message to the AI Agent"
            class="max-h-[120px] flex-1 resize-none bg-transparent py-1.5 text-sm leading-6 text-foreground outline-none placeholder:text-muted-foreground"
            @keydown="onKeydown"
          />
          <button
            v-if="isLoading"
            type="button"
            aria-label="Stop"
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-opacity hover:opacity-80 cursor-pointer"
            @click="stop"
          >
            <i-mdi-stop class="h-4 w-4" />
          </button>
          <button
            v-else
            type="submit"
            aria-label="Send"
            :disabled="!input.trim()"
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-all hover:scale-105 disabled:scale-100 disabled:bg-muted disabled:text-muted-foreground cursor-pointer disabled:cursor-default"
          >
            <i-mdi-arrow-up class="h-4 w-4" />
          </button>
        </form>
        <div class="flex flex-wrap items-center justify-center gap-1.5">
          <span class="mr-0.5 text-xs text-muted-foreground">Talk to a human:</span>
          <a
            :href="TELEGRAM_URL"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-1.5 rounded-full border border-border bg-muted/40 py-1 pl-1 pr-2.5 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-primary/10"
          >
            <img src="/icons/telegram.png" alt="" class="h-5 w-5" />
            Telegram
          </a>
          <a
            :href="whatsappUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-1.5 rounded-full border border-border bg-muted/40 py-1 pl-1 pr-2.5 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-primary/10"
          >
            <img src="/icons/whatsapp.png" alt="" class="h-5 w-5" />
            WhatsApp
          </a>
        </div>
      </footer>
    </section>

    <!-- Launcher -->
    <div
      class="fixed bottom-[calc(1rem+var(--bt-bottom-nav,0px))] right-4 z-[60] flex items-center gap-2.5 md:bottom-[calc(2rem+var(--bt-bottom-nav,0px))] md:right-8"
      :class="{ 'max-sm:hidden': open }"
    >
      <button
        type="button"
        class="max-sm:hidden rounded-full border border-border bg-background/80 px-3 py-1.5 text-xs font-semibold text-foreground shadow-sm backdrop-blur-md transition-all duration-300 hover:border-primary/50 cursor-pointer"
        :class="{ 'pointer-events-none translate-x-2 opacity-0': open }"
        @click="open = true"
      >
        Support
      </button>
      <button
        type="button"
        :aria-label="open ? 'Close' : 'Support'"
        :aria-expanded="open"
        class="relative flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-black shadow-lg shadow-primary/20 ring-2 ring-primary/60 transition-transform duration-300 hover:scale-105 active:scale-95 cursor-pointer"
        @click="open = !open"
      >
        <span class="absolute inset-1 overflow-hidden rounded-full">
          <video
            ref="orbEl"
            autoplay
            loop
            muted
            playsinline
            class="h-full w-full object-contain"
            src="/videos/orbe_xiomex_dark.webm"
          />
        </span>
        <!-- Covers the orb instead of hiding it, so the browser keeps it playing -->
        <span
          class="absolute inset-0 flex items-center justify-center rounded-full bg-black transition-opacity duration-300"
          :class="open ? 'opacity-100' : 'opacity-0'"
        >
          <i-mdi-chevron-down
            class="h-6 w-6 text-primary transition-transform duration-300"
            :class="open ? 'rotate-0 scale-100' : '-rotate-90 scale-50'"
          />
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.support-panel {
  transform-origin: bottom right;
  transition-property: opacity, transform, translate, scale;
  transition-duration: 300ms;
  transition-timing-function: cubic-bezier(0.32, 0.72, 0, 1);
}

.support-scroll {
  scrollbar-width: thin;
  scrollbar-color: hsl(var(--border)) transparent;
}

.support-glow {
  background: radial-gradient(ellipse at top, hsl(var(--primary) / 0.18), transparent 70%);
}

.support-logo {
  filter: invert(1);
}

:global(.dark .support-logo) {
  filter: none;
}

.fade-up {
  animation: fade-up 0.25s ease both;
}

@keyframes fade-up {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
